// Операции над таблицей СТО (stoTable > table): головка, выравнивание граф, прочерки, сортировка, ширины граф,
// графа «№ п/п» -> номера в боковике, преобразование текста в таблицу. Всё - одной транзакцией (одна отмена).
import type { Editor, JSONContent } from '@tiptap/core';
import type { Node as PMNode, Schema } from '@tiptap/pm/model';
import type { Transaction } from '@tiptap/pm/state';
import { TableMap, CellSelection, removeColumn } from '@tiptap/pm/tables';
import { TEXT_W_PX } from '../util';
import { NBSP } from '../../../core/schema/constants';

export type Align = 'left' | 'center' | 'right' | null;
export const DASH = '–';
/** Числовое значение ячейки (как NUMERIC_CELL_RE в lib/sto_lib.py). */
export const NUMERIC_RE = /^[\d\s.,–−\-+%×x/:()<>≤≥±]+$/;
/** Графа «№ п/п» (4.9.13) - как в writer/builder.py и lib/sto_check.py. */
export const NPP_RE = /^(№|n|no)?п[/.]?п\.?$|^№$|^№пп$|^номерпопорядку$|^п\/п$|^номер(п\/п)?$|^n$|^no$/i;

export interface TableCtx {
  stoPos: number; sto: PMNode;
  tablePos: number; table: PMNode;
  /** позиция начала содержимого table (для TableMap: абсолютная = tableStart + map-позиция) */
  tableStart: number;
  map: TableMap;
  headerRows: number;
}

/** Таблица под курсором (или выделенная целиком / по позиции stoTable). */
export function tableCtx(editor: Editor, stoPosHint?: number): TableCtx | null {
  const { state } = editor;
  let stoPos = -1;
  if (stoPosHint !== undefined) stoPos = stoPosHint;
  else {
    const $f = state.selection.$from;
    for (let d = $f.depth; d > 0; d--) if ($f.node(d).type.name === 'stoTable') { stoPos = $f.before(d); break; }
    const sel = state.selection as any;
    if (stoPos < 0 && sel.node?.type.name === 'stoTable') stoPos = sel.from;
    if (stoPos < 0 && sel.node?.type.name === 'table') stoPos = state.doc.resolve(sel.from).before();
  }
  if (stoPos < 0) return null;
  return tableCtxAt(state.doc, stoPos);
}

export function tableCtxAt(doc: PMNode, stoPos: number): TableCtx | null {
  const sto = doc.nodeAt(stoPos);
  if (!sto || sto.type.name !== 'stoTable') return null;
  let tablePos = -1;
  sto.forEach((c, off) => { if (c.type.name === 'table') tablePos = stoPos + 1 + off; });
  if (tablePos < 0) return null;
  const table = doc.nodeAt(tablePos)!;
  return { stoPos, sto, tablePos, table, tableStart: tablePos + 1, map: TableMap.get(table), headerRows: headerRowCount(table) };
}

/** Число строк головки: ведущие строки, в которых все ячейки - tableHeader (как в экспорте nodes_objects.py). */
export function headerRowCount(table: PMNode): number {
  let n = 0;
  for (let i = 0; i < table.childCount; i++) {
    const row = table.child(i);
    let all = row.childCount > 0;
    row.forEach((c) => { if (c.type.name !== 'tableHeader') all = false; });
    if (!all) break;
    n++;
  }
  return n;
}

/** Графы (left..right), затронутые выделением: CellSelection или ячейка под курсором. */
export function selectedCols(editor: Editor, t: TableCtx): { left: number; right: number; top: number; bottom: number } | null {
  const sel = editor.state.selection;
  if (sel instanceof CellSelection) {
    const a = sel.$anchorCell.pos - t.tableStart; const h = sel.$headCell.pos - t.tableStart;
    return t.map.rectBetween(a, h);
  }
  const $f = sel.$from;
  for (let d = $f.depth; d > 0; d--) {
    const n = $f.node(d);
    if (n.type.name === 'tableCell' || n.type.name === 'tableHeader') {
      const rel = $f.before(d) - t.tableStart;
      return t.map.findCell(rel);
    }
  }
  return null;
}

/** Все ячейки таблицы: {pos (абсолютная), node, row, col, colspan, rowspan}. */
export function cells(t: TableCtx): { pos: number; node: PMNode; row: number; col: number }[] {
  const out: { pos: number; node: PMNode; row: number; col: number }[] = [];
  const seen = new Set<number>();
  for (let r = 0; r < t.map.height; r++) {
    for (let c = 0; c < t.map.width; c++) {
      const rel = t.map.map[r * t.map.width + c];
      if (seen.has(rel)) continue;
      seen.add(rel);
      const node = t.table.nodeAt(rel)!;
      out.push({ pos: t.tableStart + rel, node, row: r, col: c });
    }
  }
  return out;
}

/** Задать число строк головки (1..строк-1): ячейки этих строк - tableHeader, остальные - tableCell. */
export function setHeaderRows(editor: Editor, n: number): boolean {
  const t = tableCtx(editor);
  if (!t) return false;
  const tr = editor.state.tr;
  applyHeaderRows(tr, t, n);
  if (!tr.docChanged) return true;
  editor.view.dispatch(tr);
  return true;
}

export function applyHeaderRows(tr: Transaction, t: TableCtx, n: number) {
  const schema = tr.doc.type.schema;
  const rows = t.table.childCount;
  n = Math.max(1, Math.min(n, Math.max(1, rows - 1)));
  let pos = t.tablePos + 1;
  for (let r = 0; r < rows; r++) {
    const row = t.table.child(r);
    let cp = pos + 1;
    row.forEach((cell) => {
      const want = r < n ? schema.nodes.tableHeader : schema.nodes.tableCell;
      if (cell.type !== want) tr.setNodeMarkup(tr.mapping.map(cp), want, r < n ? { ...cell.attrs, align: null } : cell.attrs);
      cp += cell.nodeSize;
    });
    pos += row.nodeSize;
  }
}

/** Выравнивание графы (4.9.9): одинаково для всех ячеек данных графы; null - авто по СТО. */
export function alignColumns(editor: Editor, al: Align): boolean {
  const t = tableCtx(editor);
  if (!t) return false;
  const rect = selectedCols(editor, t);
  if (!rect) return false;
  const tr = editor.state.tr;
  for (const c of cells(t)) {
    if (c.row < t.headerRows) continue;
    if (c.col >= rect.left && c.col < rect.right && c.node.attrs.align !== al) tr.setNodeMarkup(c.pos, undefined, { ...c.node.attrs, align: al });
  }
  editor.view.dispatch(tr);
  return true;
}

/** Текущее явное выравнивание графы под курсором (или null - авто). */
export function currentAlign(editor: Editor): Align | undefined {
  const t = tableCtx(editor);
  if (!t) return undefined;
  const rect = selectedCols(editor, t);
  if (!rect) return undefined;
  const vals = new Set(cells(t).filter((c) => c.row >= t.headerRows && c.col === rect.left).map((c) => c.node.attrs.align ?? null));
  return vals.size === 1 ? [...vals][0] : undefined;
}

function isEmptyCell(n: PMNode) {
  return !n.textContent.trim() && !hasAtoms(n);
}
function hasAtoms(n: PMNode) {
  let a = false;
  n.descendants((d) => { if (d.isAtom && !d.isText) a = true; return !a; });
  return a;
}

/** Заменить содержимое ячейки текстом. */
function setCellText(tr: Transaction, pos: number, cell: PMNode, text: string) {
  const schema = tr.doc.type.schema;
  const p = schema.nodes.paragraph.create(null, text ? schema.text(text) : null);
  tr.replaceWith(tr.mapping.map(pos) + 1, tr.mapping.map(pos + cell.nodeSize) - 1, p);
}

/** Прочерк «–» (4.9.9, 4.9.21): в выделенные/текущую пустую ячейку; в ячейке с одним прочерком - убрать его. */
export function toggleDash(editor: Editor): boolean {
  const t = tableCtx(editor);
  if (!t) return false;
  const rect = selectedCols(editor, t);
  if (!rect) return false;
  const tr = editor.state.tr;
  const target = cells(t).filter((c) => c.row >= rect.top && c.row < rect.bottom && c.col >= rect.left && c.col < rect.right && c.row >= t.headerRows);
  const allDash = target.length > 0 && target.every((c) => /^[-–—]$/.test(c.node.textContent.trim()));
  for (const c of target) {
    if (allDash) setCellText(tr, c.pos, c.node, '');
    else if (isEmptyCell(c.node)) setCellText(tr, c.pos, c.node, DASH);
  }
  if (!tr.docChanged && target.length === 1 && !allDash) {
    // одна непустая ячейка: заменить содержимое прочерком нельзя без потери данных - ничего не делаем
    return false;
  }
  editor.view.dispatch(tr);
  return true;
}

/** Все пустые ячейки данных (кроме боковика) -> «–». Возвращает число заполненных. */
export function fillEmptyWithDash(editor: Editor, stoPos?: number): number {
  const t = tableCtx(editor, stoPos);
  if (!t) return 0;
  const tr = editor.state.tr;
  let n = 0;
  for (const c of cells(t)) {
    if (c.row >= t.headerRows && c.col > 0 && isEmptyCell(c.node)) { setCellText(tr, c.pos, c.node, DASH); n++; }
  }
  if (n) editor.view.dispatch(tr);
  return n;
}

/** Число из текста ячейки ('1 234,5' -> 1234.5) или null. */
export function cellNumber(s: string): number | null {
  const t = s.replace(/[\s ]/g, '').replace(',', '.').replace(/[−–]/g, '-');
  if (!/^[-+]?\d*\.?\d+(e[-+]?\d+)?%?$/i.test(t)) return null;
  return parseFloat(t);
}

/** Сортировка строк данных по графе под курсором (числа - как числа, текст - по алфавиту). */
export function sortRows(editor: Editor, dir: 1 | -1): string | null {
  const t = tableCtx(editor);
  if (!t) return 'Нет таблицы';
  const rect = selectedCols(editor, t);
  if (!rect) return 'Поставьте курсор в графу, по которой сортировать';
  const col = rect.left;
  const rows: { node: PMNode; key: string }[] = [];
  for (let r = t.headerRows; r < t.table.childCount; r++) {
    const row = t.table.child(r);
    let bad = false;
    row.forEach((c) => { if ((c.attrs.rowspan || 1) > 1) bad = true; });
    if (bad) return 'В строках данных есть объединения по вертикали - сортировка невозможна';
    const rel = t.map.map[r * t.map.width + col];
    rows.push({ node: row, key: t.table.nodeAt(rel)?.textContent.trim() || '' });
  }
  // объединения сверху в строки данных (rowspan из головки) тоже мешают
  const sorted = [...rows].sort((a, b) => {
    const x = cellNumber(a.key); const y = cellNumber(b.key);
    if (x !== null && y !== null) return (x - y) * dir;
    if (x !== null) return -1 * dir;
    if (y !== null) return 1 * dir;
    return a.key.localeCompare(b.key, 'ru', { numeric: true, sensitivity: 'base' }) * dir;
  });
  let from = t.tablePos + 1;
  for (let r = 0; r < t.headerRows; r++) from += t.table.child(r).nodeSize;
  const to = t.tablePos + t.table.nodeSize - 1;
  const tr = editor.state.tr.replaceWith(from, to, sorted.map((r) => r.node));
  editor.view.dispatch(tr);
  return null;
}

/** Ширины граф (px, сумма = ширине поля набора) -> colwidth ячеек. null - авто (по содержимому, как writer). */
export function applyWidths(tr: Transaction, t: TableCtx, widths: number[] | null) {
  for (const c of cells(t)) {
    const span = c.node.attrs.colspan || 1;
    const cw = widths ? widths.slice(c.col, c.col + span).map((x) => Math.round(x)) : null;
    const cur = c.node.attrs.colwidth;
    if (JSON.stringify(cur ?? null) !== JSON.stringify(cw)) tr.setNodeMarkup(tr.mapping.map(c.pos), undefined, { ...c.node.attrs, colwidth: cw });
  }
}

/** Текущие ширины граф из colwidth (null, если хотя бы одна графа без ширины). */
export function currentWidths(t: TableCtx): (number | null)[] {
  const out: (number | null)[] = Array(t.map.width).fill(null);
  for (const c of cells(t)) {
    const cw = c.node.attrs.colwidth as number[] | null;
    const span = c.node.attrs.colspan || 1;
    for (let k = 0; k < span; k++) if (cw && cw[k] && out[c.col + k] === null) out[c.col + k] = cw[k];
  }
  return out;
}

/** Нормировать ширины к полю набора: пустые - среднее из заданных; сумма = TEXT_W_PX. */
export function normalizeWidths(ws: (number | null)[]): number[] {
  const set = ws.filter((x): x is number => !!x && x > 0);
  const avg = set.length ? set.reduce((a, b) => a + b, 0) / set.length : TEXT_W_PX / Math.max(1, ws.length);
  const full = ws.map((x) => (x && x > 0 ? x : avg));
  const sum = full.reduce((a, b) => a + b, 0) || 1;
  const k = TEXT_W_PX / sum;
  const out = full.map((x) => Math.max(20, Math.round(x * k)));
  const diff = TEXT_W_PX - out.reduce((a, b) => a + b, 0);
  out[out.length - 1] += diff;
  return out;
}

export function setWidths(editor: Editor, mode: 'equal' | 'auto'): boolean {
  const t = tableCtx(editor);
  if (!t) return false;
  const tr = editor.state.tr;
  applyWidths(tr, t, mode === 'auto' ? null : normalizeWidths(Array(t.map.width).fill(1)));
  editor.view.dispatch(tr);
  return true;
}

/** Первая графа - «№ п/п»? */
export function hasNppColumn(table: PMNode): boolean {
  const first = table.firstChild?.firstChild;
  if (!first || table.firstChild!.childCount < 2) return false;
  const t = first.textContent.toLowerCase().replace(/[\s ]/g, '');
  return !!t && NPP_RE.test(t);
}

/** Убрать графу «№ п/п», номера перенести в боковик перед наименованием (4.9.13): «1 Наименование». */
export function removeNppColumn(editor: Editor, stoPos?: number): boolean {
  const t = tableCtx(editor, stoPos);
  if (!t || !hasNppColumn(t.table)) return false;
  const tr = editor.state.tr;
  // номера из первой графы (если там числа) или по порядку
  const nums: string[] = [];
  for (let r = t.headerRows; r < t.map.height; r++) {
    const rel = t.map.map[r * t.map.width];
    const txt = (t.table.nodeAt(rel)?.textContent || '').trim().replace(/\.$/, '');
    nums.push(/^\d+(\.\d+)*$/.test(txt) ? txt : String(r - t.headerRows + 1));
  }
  removeColumn(tr, { map: t.map, table: t.table, tableStart: t.tableStart } as any, 0);
  const t2 = tableCtxAt(tr.doc, t.stoPos)!;
  const seen = new Set<number>();
  for (let r = t2.map.height - 1; r >= t2.headerRows; r--) {
    const rel = t2.map.map[r * t2.map.width];
    if (seen.has(rel)) continue;
    seen.add(rel);
    const n = nums[r - t2.headerRows];
    if (n) tr.insertText(`${n}${NBSP}`, t2.tableStart + rel + 2);     // «1 Сервер» (неразрывный - только при USE_NBSP)
  }
  // ширины граф - заново по содержимому
  applyWidths(tr, tableCtxAt(tr.doc, t.stoPos)!, null);
  editor.view.dispatch(tr);
  return true;
}

// ------------------------------------------------------------------------------------------------ матрица -> узлы
export interface MCell { content: JSONContent[]; colspan: number; rowspan: number; align?: Align }
export interface Matrix { rows: MCell[][]; headerRows: number }

export function textCell(text: string): MCell {
  return { content: [{ type: 'paragraph', content: text ? [{ type: 'text', text }] : [] }], colspan: 1, rowspan: 1 };
}

/** stoTable (JSON) из матрицы: головка - первые headerRows строк. */
export function stoTableJson(m: Matrix, caption = ''): JSONContent {
  const h = Math.max(1, Math.min(m.headerRows || 1, Math.max(1, m.rows.length - 1)));
  return {
    type: 'stoTable',
    content: [
      { type: 'caption', content: caption ? [{ type: 'text', text: caption }] : [] },
      {
        type: 'table',
        content: m.rows.map((row, r) => ({
          type: 'tableRow',
          content: row.map((c) => ({
            type: r < h ? 'tableHeader' : 'tableCell',
            attrs: { colspan: c.colspan, rowspan: c.rowspan, align: r < h ? null : c.align ?? null },
            content: c.content.length ? c.content : [{ type: 'paragraph' }],
          })),
        })),
      },
    ],
  };
}

/** Узел table (для вставки ячеек в существующую таблицу). header - какие строки сделать головкой. */
export function tableNodeFromMatrix(schema: Schema, m: Matrix, headerUpTo = 0): PMNode {
  const json: JSONContent = {
    type: 'table',
    content: m.rows.map((row, r) => ({
      type: 'tableRow',
      content: row.map((c) => ({
        type: r < headerUpTo ? 'tableHeader' : 'tableCell',
        attrs: { colspan: c.colspan, rowspan: c.rowspan },
        content: c.content.length ? c.content : [{ type: 'paragraph' }],
      })),
    })),
  };
  return schema.nodeFromJSON(json);
}

/** Текст -> матрица: разделитель - табуляция, «;», «|» или «,» (определяется сам), кавычки CSV. */
export function parseDelimited(text: string, delim?: string): Matrix | null {
  const src = text.replace(/\r\n?/g, '\n').replace(/\n+$/, '');
  if (!src.trim()) return null;
  const lines = src.split('\n');
  const d = delim || detectDelimiter(lines);
  if (!d) return null;
  const rows = parseCsv(src, d).map((r) => (d === '|' ? stripPipes(r) : r)).filter((r, i, a) => !(d === '|' && r.every((c) => /^:?-{2,}:?$/.test(c.trim())) && i === 1 && a.length >= 2));
  const width = Math.max(...rows.map((r) => r.length));
  if (width < 1) return null;
  const mrows = rows.map((r) => Array.from({ length: width }, (_, i) => textCell((r[i] ?? '').trim())));
  return trimMatrix({ rows: mrows, headerRows: 1 });
}

function stripPipes(r: string[]) {
  const a = [...r];
  if (a.length && !a[0].trim()) a.shift();
  if (a.length && !a[a.length - 1].trim()) a.pop();
  return a;
}

export function detectDelimiter(lines: string[]): string | null {
  const ls = lines.filter((l) => l.trim());
  if (!ls.length) return null;
  for (const d of ['\t', '|', ';', ',']) {
    const counts = ls.map((l) => countOutsideQuotes(l, d));
    if (counts.every((c) => c > 0) && (d === '\t' || d === '|' || new Set(counts).size === 1)) return d;
  }
  // один столбец (строки без разделителей)
  return ls.length > 1 ? '\t' : null;
}

function countOutsideQuotes(line: string, d: string) {
  let n = 0; let q = false;
  for (const ch of line) { if (ch === '"') q = !q; else if (ch === d && !q) n++; }
  return n;
}

/** CSV/TSV по RFC 4180: кавычки, удвоенные кавычки, переводы строк внутри кавычек. */
export function parseCsv(src: string, d: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = []; let cell = ''; let q = false;
  for (let i = 0; i < src.length; i++) {
    const ch = src[i];
    if (q) {
      if (ch === '"') { if (src[i + 1] === '"') { cell += '"'; i++; } else q = false; } else cell += ch;
    } else if (ch === '"' && cell.trim() === '') { q = true; cell = ''; }
    else if (ch === d) { row.push(cell); cell = ''; }
    else if (ch === '\n') { row.push(cell); rows.push(row); row = []; cell = ''; }
    else cell += ch;
  }
  row.push(cell); rows.push(row);
  return rows;
}

/** Убрать пустые строки и графы по краям. */
export function trimMatrix(m: Matrix): Matrix {
  const empty = (c: MCell) => c.colspan === 1 && c.rowspan === 1 && !JSON.stringify(c.content).match(/"text":"[^"]*\S/);
  const rows = m.rows.filter((r, i) => !(r.every(empty) && (i === m.rows.length - 1 || i === 0)) || m.rows.length === 1);
  while (rows.length > 1 && rows[rows.length - 1].every(empty)) rows.pop();
  const simple = rows.every((r) => r.every((c) => c.colspan === 1 && c.rowspan === 1));
  if (simple && rows.length) {
    let w = Math.max(...rows.map((r) => r.length));
    while (w > 1 && rows.every((r) => !r[w - 1] || empty(r[w - 1]))) { rows.forEach((r) => r.splice(w - 1, 1)); w--; }
  }
  return { rows, headerRows: Math.min(m.headerRows, Math.max(1, rows.length - 1)) };
}
